// Tiny Web Audio synth for game SFX. Deliberately no audio files: synthesised
// tones are CSP-safe (no external fetch), work offline, add zero bytes to the
// bundle, and can't 404. AudioContext is created lazily on first play so we
// never trip the browser autoplay policy (which requires a user gesture).

const MUTE_KEY = 'bf_muted';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  // A context created before a gesture starts "suspended"; resume on demand.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function toggleMuted(): boolean {
  const next = !isMuted();
  setMuted(next);
  return next;
}

interface ToneSpec {
  freq: number;
  start: number; // seconds from now
  duration: number;
  type?: OscillatorType;
  gain?: number;
  slideTo?: number; // optional target frequency for a pitch glide
}

function playTones(tones: ToneSpec[]): void {
  if (isMuted()) return;
  const audio = getCtx();
  if (!audio) return;
  const now = audio.currentTime;

  for (const t of tones) {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = t.type || 'sine';
    osc.frequency.setValueAtTime(t.freq, now + t.start);
    if (t.slideTo) {
      osc.frequency.exponentialRampToValueAtTime(t.slideTo, now + t.start + t.duration);
    }
    const peak = t.gain ?? 0.12;
    // Quick attack, exponential release — keeps it a clean blip, not a click.
    gain.gain.setValueAtTime(0.0001, now + t.start);
    gain.gain.exponentialRampToValueAtTime(peak, now + t.start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.duration);
    osc.connect(gain);
    gain.connect(audio.destination);
    osc.start(now + t.start);
    osc.stop(now + t.start + t.duration + 0.02);
  }
}

// Haptics: a short buzz on answers (Android; iPhone Safari has no vibration
// API, so this is a no-op there). Follows the phone's own vibration setting,
// separate from the in-app sound mute.
function buzz(pattern: number | number[]): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
  } catch { /* ignore */ }
}

export function playCorrect(): void {
  buzz(18);
  playTones([
    { freq: 660, start: 0, duration: 0.12, type: 'triangle' },
    { freq: 990, start: 0.09, duration: 0.16, type: 'triangle' },
  ]);
}

export function playWrong(): void {
  buzz([35, 60, 35]);
  playTones([
    { freq: 220, start: 0, duration: 0.22, type: 'sawtooth', gain: 0.09, slideTo: 130 },
  ]);
}

export function playStreak(): void {
  buzz([15, 40, 15, 40, 25]);
  playTones([
    { freq: 660, start: 0, duration: 0.1, type: 'triangle' },
    { freq: 880, start: 0.08, duration: 0.1, type: 'triangle' },
    { freq: 1320, start: 0.16, duration: 0.18, type: 'triangle' },
  ]);
}

export function playFinish(): void {
  buzz([20, 50, 40]);
  playTones([
    { freq: 523, start: 0, duration: 0.14, type: 'triangle' },
    { freq: 659, start: 0.12, duration: 0.14, type: 'triangle' },
    { freq: 784, start: 0.24, duration: 0.14, type: 'triangle' },
    { freq: 1047, start: 0.36, duration: 0.28, type: 'triangle' },
  ]);
}

export function playTick(): void {
  playTones([{ freq: 800, start: 0, duration: 0.04, type: 'square', gain: 0.05 }]);
}
